import { defineComponent as _defineComponent } from 'vue'
import { DataTableHeader } from "vuetify";

import AdditionalRoleOffice from "@/components/profile/AdditionalRoleOffice.vue";
import AdditionalRolesRow from "@/components/profile/AdditionalRolesRow.vue";
import ArchivedIcon from "@/components/ArchivedIcon.vue";
import BriefcaseDialog from "@/components/BriefcaseFolderMoveDialog.vue";
import ExportButton from "@/components/buttons/ExportButton.vue";
import OfficeAddress from "@/components/profile/OfficeAddress.vue";
import ProfileCard from "@/components/ProfileCard.vue";
import ProfileDownloadControls from "@/components/profile/ProfileDownloadControls.vue";
import ProfileItem from "@/components/profile/ProfileItem.vue";
import ProfilePanel from "@/components/profile/ProfilePanel.vue";
import ProfileSubModuleCards from "@/components/profile/ProfileSubModuleCards.vue";
import ProfileWrapper from "@/components/profile/ProfileWrapper.vue";
import { ApiService, LogService, SnackbarService } from "@/services";
import {
  MetadataKeys,
  Profile as ProfileObj,
  RedirectResponse,
  VignetteHelpers,
  VignetteRoutes,
} from "@/types";
import { useAppStore } from "@/pinia/app";
import { computed, onBeforeMount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { useViewAnalytics } from "@/composition/useViewAnalytics";
import ElectionStatus from "@/components/ElectionStatus.vue";
import DjangoRouterLink from "@/components/links/DjangoRouterLink.vue";
import { Chip } from "@/types/nj/Chip";
import HierarchyChip from "@/components/profile/HierarchyChip.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileDetail',
  setup(__props) {

const route = useRoute();
const router = useRouter();

// <editor-fold desc="App Store Properties">
const appStore = useAppStore();
// </editor-fold>

// <editor-fold desc="Profile Properties">
const profile = ref<ProfileObj | null>(null);
const isLoaded = ref(false);
const modules = computed<number[]>(
  () => profile.value?.subscriptionModules || []
);
const socialLinks = computed<
  { title: string; link: string; display?: string }[]
>(() => {
  const facebook = {
    title: "Facebook",
    link: facebookLink.value,
  };
  const twitter = {
    title: "X (Twitter)",
    link: twitterLink.value,
  };
  const youtube = {
    title: "YouTube",
    link: youtubeLink.value,
  };
  const instagram = {
    title: "Instagram",
    link: instagramLink.value,
    display: instagramHandle.value,
  };
  const linkedIn = {
    title: "LinkedIn",
    link: linkedInLink.value,
  };
  const website = {
    title: "Website",
    link: websiteLink.value,
  };
  return [
    ...(facebookLink.value ? [facebook] : []),
    ...(twitterLink.value ? [twitter] : []),
    ...(youtubeLink.value ? [youtube] : []),
    ...(instagramLink.value ? [instagram] : []),
    ...(linkedInLink.value ? [linkedIn] : []),
    ...(websiteLink.value ? [website] : []),
  ];
});
const facebookLink = computed(() => profile.value?.facebookUrl ?? "");
const getTwitterLinkFromString = (twitterString: string): string => {
  if (twitterString) {
    if (twitterString.includes("http")) {
      return twitterString.replace("twitter.com", "x.com");
    } else if (twitterString.includes("@")) {
      return `https://x.com/${twitterString.replace("@", "")}`;
    } else {
      return `https://x.com/${twitterString}`;
    }
  }
  return "";
};
const twitterLink = computed(() => {
  if (profile.value?.twitterUsername) {
    return getTwitterLinkFromString(profile.value.twitterUsername);
  }
  return "";
});
const instagramLink = computed(() =>
  profile.value?.instagramUsername
    ? `https://www.instagram.com/${profile.value.instagramUsername}`
    : ""
);
const instagramHandle = computed(() =>
  profile.value?.instagramUsername ? `@${profile.value.instagramUsername}` : ""
);
const youtubeLink = computed(() => profile.value?.youtube ?? "");
const linkedInLink = computed(() => profile.value?.linkedInUrl ?? "");
const websiteLink = computed(() => profile.value?.websiteUrl ?? "");
const emailLink = computed(() =>
  profile.value?.email.includes("@")
    ? `mailto:${profile.value.email}`
    : profile.value?.email ?? ""
);

/**
 * Computes whether to show the electoral history section.
 * If the electoral history is not empty, returns true.
 * Otherwise, returns true if the first elected date and number of terms are set.
 */
const showElectoralHistory = computed<boolean>(() => {
  const electoralHistory = profile.value?.electoralHistory ?? [];
  return electoralHistory.length > 0 || !!firstElectedWithTenureText.value;
});

/**
 * Computes the text to display for the first elected with tenure.
 * If the first elected date is not set, returns an empty string (even if the number of terms is set).
 * If the number of terms is not set, returns just the first elected date.
 * If the number of terms is 0, returns "${firstElectedDate}, First Term".
 * If the number of terms is 1, returns "${firstElectedDate}, 1 term".
 * Otherwise, returns "${firstElectedDate}, ${numberOfTerms} terms".
 */
const firstElectedWithTenureText = computed(() => {
  let firstElected = profile.value?.firstElected.trim() ?? "";
  const tenure = profile.value?.numberTenuresInSeat ?? 0;
  const getTenureString = (tenure: number): string => {
    if (!tenure) return "";
    if (tenure === 1) return "First Term";
    return tenure + " terms";
  };
  if (!!firstElected && !!tenure) firstElected += ",";
  return `${firstElected} ${getTenureString(tenure)}`.trim();
});

const showInBrief = computed(() => {
  if (!profile.value) return false;
  return (
    profile.value.phoneticName ||
    profile.value.birthDate ||
    profile.value.birthplace ||
    profile.value.hometown ||
    profile.value.residences.length > 0 ||
    profile.value.religion ||
    profile.value.ethnicity ||
    profile.value.education.length > 0 ||
    profile.value.educationSummary ||
    profile.value.maritalStatus ||
    profile.value.familyMembers ||
    profile.value.title ||
    profile.value.politicalParty ||
    profile.value.electoralHistory.length > 0 ||
    profile.value.workHistory ||
    profile.value.electoralOverview
  );
});
// </editor-fold>

// <editor-fold desc="UI Properties">
const display404 = ref(false);
const display500 = ref(false);
const panelsToBeOpened = ref<string[]>([
  "Quick Summary",
  "Congressional Information",
  "Legislative Information",
  "Congressional Staff",
  "Legislative Staff",
  "Gubernatorial Staff",
  "Web and Social Media",
  "Office Contact Information",
]);

// </editor-fold>

const pageName = ref("");
const { pageTitle, setAnalyticsMetadata, pushPageViewEvent } =
  useViewAnalytics(pageName);

const congressionalStaffersHeaders = ref<DataTableHeader[]>([
  {
    text: "Name",
    value: "stafferFullName",
    sortable: true,
    filterable: true,
    align: "start",
    sort: (a: string, b: string) => {
      return a.localeCompare(b);
    },
  },
  {
    text: "Job Categories",
    value: "rolenames",
    sortable: true,
    filterable: true,
    align: "start",
  },
]);

const focusAreaHeader: DataTableHeader = {
  text: "Focus Areas",
  value: "stafferInterests",
  sortable: true,
  filterable: true,
  align: "start",
};
const stafferSearch = ref("");

const formatCurrency = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "(Unavailable)";
  }
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const hierarchyChips = computed<Chip[]>(() => profile.value?.chips ?? []);


const getAllCmsTags = (): string => {
  let result = "";

  if (profile.value != null) {
    if (!!profile.value.primaryProfileType) {
      result += profile.value.primaryProfileType;
    }
    if (profile.value.primaryProfileTypesAdditional.length > 0) {
      if (result.length > 0) result += ",";
      result += profile.value.primaryProfileTypesAdditional.join(",");
    }
    if (profile.value.secondaryProfileTypes.length > 0) {
      if (result.length > 0) result += ",";
      result += profile.value.secondaryProfileTypes.join(",");
    }
    if (profile.value.subscriptionModules.length > 0) {
      if (result.length > 0) result += ",";
      result += profile.value.subscriptionModules
        .map((module) => appStore.getSubscriptionModuleName(module))
        .join(",");
    }
  }
  return result;
};

const contentString = computed<string>(() => {
  if (!profile.value?.id) return "";
  if (route.name === VignetteRoutes.ProfileKW) {
    return `knowwho-person-${profile.value.id}`;
  } else {
    return `profile-${profile.value.id}`;
  }
});
const setAnalyticsMetadataAndSendEvent = (): void => {
  setAnalyticsMetadata(MetadataKeys.SITESECTION, "AlmanacPlus");
  if (profile.value != null) {
    setAnalyticsMetadata(MetadataKeys.PAGE_TYPE, "Database");
    setAnalyticsMetadata(MetadataKeys.CONTENT_TITLE, pageName.value);
    setAnalyticsMetadata(MetadataKeys.CMS_CONTENT_ID, contentString.value);
    setAnalyticsMetadata(MetadataKeys.CMS_TAGS, getAllCmsTags());
    setAnalyticsMetadata(
      MetadataKeys.VIGNETTE_PRIMARY_PROFILE_TYPE,
      profile.value.primaryProfileType
    );
    pushPageViewEvent();
  }
};

const handleHeadshotError = () => {
  if (profile.value) {
    profile.value.headshot = "";
  }
};

onBeforeMount(async () => {
  const profileIdParam = route.params.id;
  // mostly a type guard for profileIdParam
  if (!profileIdParam) {
    await router.replace({ name: VignetteRoutes.NotFound });
    return;
  }
  const profileNameParam = route.params.name;
  LogService.debug(
    `Profile.vue: profileIdParam: ${profileIdParam}, profileNameParam: ${profileNameParam}`
  );

  const profileId = +profileIdParam;

  if (isNaN(profileId)) {
    LogService.warn(
      `Profile.vue: Invalid profileId is requested: '${profileIdParam}'`
    );
  } else {
    await ApiService.refreshBriefcase();
    const { data, error, ok } = await ApiService.getProfile(
      profileId,
      route.name === VignetteRoutes.ProfileKW
    );
    if (ok) {
      profile.value = data;
      if (profile.value != null) {
        pageName.value = profile.value.preferredNameDisplay
          ? profile.value.preferredNameDisplay
          : "";
      }
      const hasFocusAreas = profile.value?.staffers?.some(
        (staffer) => staffer.stafferInterests?.length > 0
      );
      if (hasFocusAreas) {
        congressionalStaffersHeaders.value.push(focusAreaHeader);
      }
      setAnalyticsMetadataAndSendEvent();
    } else {
      if (error instanceof RedirectResponse) {
        await router.replace(error.targetUrl);
        return;
      }
      const status = error?.code;
      LogService.error(`ProfileDetail.vue: Error retrieving profile: ${error}`);
      if (status && Number(status) >= 500) {
        display500.value = true;
      } else {
        display404.value = true;
      }
    }
    isLoaded.value = true;
  }
});

return { __sfc: true,route, router, appStore, profile, isLoaded, modules, socialLinks, facebookLink, getTwitterLinkFromString, twitterLink, instagramLink, instagramHandle, youtubeLink, linkedInLink, websiteLink, emailLink, showElectoralHistory, firstElectedWithTenureText, showInBrief, display404, display500, panelsToBeOpened, pageName, pageTitle, setAnalyticsMetadata, pushPageViewEvent, congressionalStaffersHeaders, focusAreaHeader, stafferSearch, formatCurrency, hierarchyChips, getAllCmsTags, contentString, setAnalyticsMetadataAndSendEvent, handleHeadshotError, AdditionalRoleOffice, AdditionalRolesRow, ArchivedIcon, BriefcaseDialog, ExportButton, OfficeAddress, ProfileCard, ProfileDownloadControls, ProfileItem, ProfilePanel, ProfileSubModuleCards, ProfileWrapper, VignetteHelpers, VignetteRoutes, ElectionStatus, DjangoRouterLink, HierarchyChip }
}

})